import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { trpc } from "../../lib/trpc";

export type PartyKind = "supplier" | "customer";

interface PartyFormModalProps {
  open: boolean;
  kind: PartyKind;
  initial?: ExistingParty | null;
  onClose: () => void;
  /** Yeni kayıt oluşturulduğunda (düzenleme değil) tetiklenir — çağrı yerinde otomatik seçim için. */
  onCreated?: (party: { id: string; code: string; name: string }) => void;
}

interface ExistingParty {
  id: string;
  code: string;
  name: string;
  taxNumber: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
}

interface FormState {
  code: string;
  name: string;
  taxNumber: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
}

const EMPTY: FormState = {
  code: "",
  name: "",
  taxNumber: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  country: "TR",
};

function toFormState(initial: ExistingParty | null | undefined): FormState {
  if (!initial) return EMPTY;
  return {
    code: initial.code,
    name: initial.name,
    taxNumber: initial.taxNumber ?? "",
    email: initial.email ?? "",
    phone: initial.phone ?? "",
    address: initial.address ?? "",
    city: initial.city ?? "",
    country: initial.country ?? "TR",
  };
}

const KIND_TITLE: Record<PartyKind, { create: string; edit: string; codeHint: string }> = {
  supplier: { create: "Yeni Tedarikçi", edit: "Tedarikçi Düzenle", codeHint: "SUP-001" },
  customer: { create: "Yeni Müşteri", edit: "Müşteri Düzenle", codeHint: "CUS-001" },
};

export function PartyFormModal({ open, kind, initial, onClose, onCreated }: PartyFormModalProps) {
  const isEdit = !!initial;
  const [state, setState] = useState<FormState>(EMPTY);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    if (open) {
      setState(toFormState(initial));
      setSubmitErr(null);
    }
  }, [open, initial]);

  const createSupplier = trpc.supplier.create.useMutation();
  const updateSupplier = trpc.supplier.update.useMutation();
  const createCustomer = trpc.customer.create.useMutation();
  const updateCustomer = trpc.customer.update.useMutation();

  // Create'te kod otomatik: müşteri CUS-{ad}-{sıra}, tedarikçi SUP-{ad}-{sıra}. Düzenlemede sabit.
  const autoCode = !isEdit;
  const [debouncedName, setDebouncedName] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(state.name.trim()), 300);
    return () => clearTimeout(t);
  }, [state.name]);
  const supPreviewQ = trpc.supplier.codePreview.useQuery(
    { name: debouncedName },
    { enabled: open && autoCode && kind === "supplier" && debouncedName.length > 0, staleTime: 3_000 },
  );
  const cusPreviewQ = trpc.customer.codePreview.useQuery(
    { name: debouncedName },
    { enabled: open && autoCode && kind === "customer" && debouncedName.length > 0, staleTime: 3_000 },
  );
  const previewCode = autoCode ? ((kind === "supplier" ? supPreviewQ.data?.code : cusPreviewQ.data?.code) ?? "") : state.code;

  const isPending =
    createSupplier.isPending || updateSupplier.isPending ||
    createCustomer.isPending || updateCustomer.isPending;

  const errorMessage = useMemo(() => {
    const mut = createSupplier.error ?? updateSupplier.error ?? createCustomer.error ?? updateCustomer.error;
    if (submitErr) return submitErr;
    if (!mut) return null;
    const zod = (mut.data as { zodError?: { fieldErrors?: Record<string, string[]> } } | undefined)?.zodError?.fieldErrors;
    if (zod) {
      const msgs = Object.values(zod).flat().filter(Boolean) as string[];
      if (msgs.length) return msgs.join(" · ");
    }
    return mut.message ?? "Bilinmeyen hata";
  }, [submitErr, createSupplier.error, updateSupplier.error, createCustomer.error, updateCustomer.error]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((s) => ({ ...s, [key]: value }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitErr(null);
    if (!autoCode && !state.code.trim()) return setSubmitErr("Kod zorunlu.");
    if (!state.name.trim()) return setSubmitErr("Ad zorunlu.");

    try {
      let created: { id: string; code: string; name: string } | undefined;
      if (kind === "supplier") {
        if (isEdit && initial) {
          await updateSupplier.mutateAsync({
            id: initial.id,
            name: state.name,
            taxNumber: state.taxNumber || null,
            email: state.email || null,
            phone: state.phone || null,
            address: state.address || null,
            city: state.city || null,
            country: state.country || "TR",
          });
        } else {
          created = await createSupplier.mutateAsync({
            code: undefined, // sunucu otomatik üretir (SUP-{ad}-{sıra})
            name: state.name,
            taxNumber: state.taxNumber || undefined,
            email: state.email || undefined,
            phone: state.phone || undefined,
            address: state.address || undefined,
            city: state.city || undefined,
            country: state.country || "TR",
          });
        }
      } else {
        if (isEdit && initial) {
          await updateCustomer.mutateAsync({
            id: initial.id,
            name: state.name,
            taxNumber: state.taxNumber || null,
            email: state.email || null,
            phone: state.phone || null,
            address: state.address || null,
            city: state.city || null,
            country: state.country || "TR",
          });
        } else {
          created = await createCustomer.mutateAsync({
            code: undefined, // sunucu otomatik üretir (CUS-{ad}-{sıra})
            name: state.name,
            taxNumber: state.taxNumber || undefined,
            email: state.email || undefined,
            phone: state.phone || undefined,
            address: state.address || undefined,
            city: state.city || undefined,
            country: state.country || "TR",
          });
        }
      }
      await qc.invalidateQueries();
      if (created) onCreated?.({ id: created.id, code: created.code, name: created.name });
      onClose();
    } catch {
      /* error in errorMessage */
    }
  }

  const titles = KIND_TITLE[kind];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEdit ? titles.edit : titles.create}
      subtitle={isEdit ? `Kod: ${initial!.code}` : "Tüm zorunlu alanları doldurun (*)"}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={isPending}>İptal</button>
          <button type="submit" form="party-form" className="btn-md primary" disabled={isPending}>
            {isPending ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur"}
          </button>
        </>
      }
    >
      <form id="party-form" onSubmit={handleSubmit} noValidate>
        {errorMessage ? <div className="form-banner-err">{errorMessage}</div> : null}

        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="pf-code">
              Kod {autoCode
                ? <span style={{ fontSize: 10.5, color: "var(--neutral-400)", fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>(otomatik)</span>
                : <span className="req">*</span>}
            </label>
            <input
              id="pf-code"
              className="form-input"
              type="text"
              value={autoCode ? previewCode : state.code}
              onChange={(e) => setField("code", e.target.value.toUpperCase())}
              placeholder={autoCode ? "Ad girince üretilir…" : titles.codeHint}
              maxLength={50}
              autoComplete="off"
              disabled={isEdit}
              readOnly={autoCode}
              required={!autoCode}
              style={autoCode ? { background: "var(--neutral-50)", color: "var(--neutral-800)", fontFamily: "var(--font-mono)", fontWeight: 600, cursor: "default" } : undefined}
              aria-readonly={autoCode || undefined}
            />
            <span className="help">{autoCode ? "Adın sessiz harfleri + sıra no ile otomatik üretilir. Sonradan değiştirilemez." : "Yarat sonrası değiştirilemez."}</span>
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="pf-name">{kind === "supplier" ? "Firma Adı" : "Ad"} <span className="req">*</span></label>
            <input
              id="pf-name"
              className="form-input"
              type="text"
              value={state.name}
              onChange={(e) => setField("name", e.target.value)}
              maxLength={200}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="pf-tax">Vergi No</label>
            <input
              id="pf-tax"
              className="form-input"
              type="text"
              value={state.taxNumber}
              onChange={(e) => setField("taxNumber", e.target.value)}
              maxLength={50}
              inputMode="numeric"
            />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="pf-email">E-posta</label>
            <input
              id="pf-email"
              className="form-input"
              type="email"
              value={state.email}
              onChange={(e) => setField("email", e.target.value)}
              autoComplete="email"
              inputMode="email"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="pf-phone">Telefon</label>
            <input
              id="pf-phone"
              className="form-input"
              type="tel"
              value={state.phone}
              onChange={(e) => setField("phone", e.target.value)}
              autoComplete="tel"
              inputMode="tel"
            />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="pf-city">Şehir</label>
            <input
              id="pf-city"
              className="form-input"
              type="text"
              value={state.city}
              onChange={(e) => setField("city", e.target.value)}
              autoComplete="address-level2"
            />
          </div>
        </div>

        <div className="form-field">
          <label className="lbl" htmlFor="pf-address">Adres</label>
          <textarea
            id="pf-address"
            className="form-textarea"
            value={state.address}
            onChange={(e) => setField("address", e.target.value)}
            maxLength={500}
            rows={2}
          />
        </div>

        <div className="form-field" style={{ maxWidth: 160 }}>
          <label className="lbl" htmlFor="pf-country">Ülke</label>
          <input
            id="pf-country"
            className="form-input"
            type="text"
            value={state.country}
            onChange={(e) => setField("country", e.target.value)}
            maxLength={100}
          />
        </div>
      </form>
    </Drawer>
  );
}
